<script lang="ts">
	import { onMount } from "svelte";
	import Media from "./Media.svelte";

	const {
		imageSrc,
		videoSrc,
		alt,
		imageWidth,
		imageHeight,
		class: className = "",
		wrapperClass = "",
		eager = false,
		playOnScroll = false,
	}: {
		imageSrc: string;
		videoSrc?: string;
		alt: string;
		imageWidth: number;
		imageHeight: number;
		class?: string;
		wrapperClass?: string;
		eager?: boolean;
		playOnScroll?: boolean;
	} = $props();

	let videoElement: HTMLVideoElement | undefined = $state();
	let isPlaying = $state(false);
	let isMobile = $state(false);

	export function play(): void {
		if (videoElement && videoSrc) {
			void videoElement.play().catch(() => {
				// Ignore play errors
			});
		}
	}

	export function stop(): void {
		if (videoElement && videoSrc) {
			videoElement.pause();
			videoElement.currentTime = 0;
		}
	}

	onMount(() => {
		isMobile = window.matchMedia("(max-width: 1023px)").matches;

		// Force load first frame immediately
		if (videoElement && videoSrc) {
			const video = videoElement;
			video.currentTime = 0.001;
		}

		if (!playOnScroll || !videoElement || !isMobile) {
			return;
		}

		const video = videoElement;

		const observer = new IntersectionObserver(
			(entries) => {
				entries.forEach((entry) => {
					if (entry.isIntersecting && !isPlaying) {
						void video.play().catch(() => {
							// Ignore play errors
						});
						isPlaying = true;
					} else if (!entry.isIntersecting && isPlaying) {
						video.pause();
						video.currentTime = 0;
						isPlaying = false;
					}
				});
			},
			{ threshold: 0.5 },
		);

		observer.observe(video);

		return (): void => {
			observer.disconnect();
		};
	});
</script>

<svelte:head>
	{#if videoSrc}
		<link rel="preload" href={videoSrc} as="video" fetchpriority={eager ? "high" : "low"} />
	{/if}
</svelte:head>

<div class={wrapperClass} role="img">
	{#if videoSrc}
		<div class="relative w-full h-full" style="isolation: isolate;">
			<video
				bind:this={videoElement}
				width={imageWidth}
				height={imageHeight}
				class="{className} absolute inset-0 w-full h-full object-cover"
				style="color-scheme: normal; -webkit-color-scheme: normal; background-color: #F7F3EF;"
				muted
				loop
				playsinline
				preload="metadata"
			>
				<source src={videoSrc} type="video/mp4" />
				<source src={videoSrc} type="video/webm" />
			</video>
		</div>
	{:else}
		<Media
			source={{ src: imageSrc, type: "image/webp" }}
			{alt}
			sourcesets={{}}
			width={imageWidth}
			height={imageHeight}
			class={className}
			{wrapperClass}
			{eager}
		/>
	{/if}
</div>
